import type { Box } from "../screen";

/**
 * Every number in this file is lifted from the Figma frame
 * "Design: Mobile Landing Page" > "Text+Layout+Color+New" (36:60) and is
 * expressed in that frame's 430x940 coordinate space. See figma-data/ at the
 * repo root for the payload and the reference render.
 *
 * The background is the whole of "Background" (36:62): two orbs, a wave, and
 * the paint inside the wave. Figma has it as an absolutely positioned,
 * clipping frame at the frame's own 430x940 — it does not stretch with the
 * screen, and neither does this. Stretching it would turn the orbs into
 * ellipses, which is the one thing the composition cannot survive.
 */

/* -------------------------------------------------------------------------
 * The wave
 * ---------------------------------------------------------------------- */

/**
 * The shape the paint fills, and the mask that keeps the rest of the screen
 * white (nodes 36:65 and 36:66 — a visible copy under an identical mask).
 * Unchanged from the previous design; the export is kept at
 * figma-data/mask-17-603.svg.
 *
 * The export's right-hand anchors read `429`; the frame is 430 wide and the
 * Figma raster paints all 430 columns, so they are snapped to `430` here.
 * Left as exported they leave a 1px white seam down the top-right edge.
 */
export const WAVE_PATH =
  "M430 71V-50H0V534C0 534 170.5 601.392 170.5 745C170.5 888.608 0 940 0 940" +
  "H430L430 642.5C430 642.5 15 579.5 15 339C15 98.5 430 71 430 71Z";

/* -------------------------------------------------------------------------
 * The paint inside the wave
 *
 * Figma stacks three layers in the wave's shape: a solid #FF0000, then a 4x4
 * mesh gradient at 76% (a code-component fill on node 36:67), then #FF9FED at
 * 30%. `waveColor` pre-composites all three, because a mesh gradient has no
 * CSS or SVG equivalent and the layers are only separable if the mesh is.
 *
 * Two details decide whether the result matches Figma's raster:
 *
 *  - The mesh interpolates in **linear-light sRGB**, not in sRGB. Sampled in
 *    sRGB the composite is out by up to 17/255; in linear light it is out by
 *    2. Compositing, by contrast, happens in sRGB — doing that in linear light
 *    puts it out by 47.
 *  - The surface through the control points is **bicubic** (Catmull-Rom), not
 *    bilinear. Bilinear costs another 8/255 on top.
 *
 * Both were fitted against figma-data/text-layout-color-new.png: the model
 * below reproduces it to a mean of 2/255 over the whole wave, and the residual
 * is concentrated where the mesh's red channel clips at zero and Figma's own
 * output goes flat.
 * ---------------------------------------------------------------------- */

/** Where the control points sit, as fractions of the frame. Figma's own 0/33/67/100. */
const MESH_AT = [0, 0.33, 0.67, 1] as const;

/** The 4x4 grid of control colours, top row first. */
const MESH = [
  ["#FF6B6B", "#FFA36B", "#FFD16B", "#FFD166"],
  ["#B24D99", "#CC8C80", "#E5B273", "#80B259"],
  ["#6680B2", "#59A6A6", "#33A68C", "#1A8CB2"],
  ["#05D6A1", "#1AB2A6", "#1A99B2", "#128AB2"],
] as const;

/** The solid #FF0000 the mesh is painted over. */
const BASE = [255, 0, 0] as const;
const MESH_ALPHA = 0.76;

/** The #FF9FED laid over the mesh. */
const TINT = [255, 159, 237] as const;
const TINT_ALPHA = 0.3;

const toLinear = (channel: number) => {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

const toSrgb = (channel: number) => {
  // Catmull-Rom overshoots past the control points, and Figma clamps rather
  // than letting a channel go negative. Where it clamps, the raster goes flat.
  const c = Math.min(Math.max(channel, 0), 1);
  return 255 * (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);
};

const MESH_LINEAR = MESH.map((row) =>
  row.map((hex) => [1, 3, 5].map((i) => toLinear(Number.parseInt(hex.slice(i, i + 2), 16)))),
);

/** Which cell of the control grid `t` lands in, and how far across it. */
function cell(t: number): readonly [index: number, fraction: number] {
  const index = Math.min(
    MESH_AT.findIndex((_, i) => i === 2 || t <= MESH_AT[i + 1]),
    2,
  );
  return [index, (t - MESH_AT[index]) / (MESH_AT[index + 1] - MESH_AT[index])];
}

/** One span of a Catmull-Rom spline through `points`, with the ends clamped. */
function spline(points: readonly number[], index: number, t: number) {
  const p0 = points[Math.max(index - 1, 0)];
  const p1 = points[index];
  const p2 = points[index + 1];
  const p3 = points[Math.min(index + 2, points.length - 1)];

  return (
    0.5 *
    (2 * p1 +
      (p2 - p0) * t +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t +
      (3 * p1 - 3 * p2 + p3 - p0) * t * t * t)
  );
}

/**
 * The colour the wave is painted at `(u, v)`, both fractions of the frame.
 * Everything Figma stacks in the wave is already composited into it.
 */
export function waveColor(u: number, v: number): string {
  const [column, across] = cell(u);
  const [row, down] = cell(v);

  const channels = [0, 1, 2].map((k) => {
    const columns = MESH_LINEAR.map((points) =>
      spline(
        points.map((c) => c[k]),
        column,
        across,
      ),
    );
    const mesh = toSrgb(spline(columns, row, down));
    const painted = MESH_ALPHA * mesh + (1 - MESH_ALPHA) * BASE[k];

    return Math.round(TINT_ALPHA * TINT[k] + (1 - TINT_ALPHA) * painted);
  });

  return `rgb(${channels.join(" ")})`;
}

/**
 * How finely `waveColor` is sampled into the gradients that draw it: one
 * vertical gradient per column, cross-faded across.
 *
 * Both counts are the smallest that hold the sampled surface to a mean of
 * 0.5/255 against the exact one. Rows past ~30 stop paying for themselves —
 * what is left is the horizontal reconstruction, which is why there are more
 * columns here than the four the mesh is defined with.
 */
export const WAVE_COLUMNS = 9;
export const WAVE_ROWS = 31;

/* -------------------------------------------------------------------------
 * The orbs (36:63, 36:64)
 *
 * Unchanged from the previous design. Both are circles of radius 103.5 with
 * the same fill: #FF0000 at 92%, with a radial gradient over it at 48%. The
 * gradient handles describe a circle of 0.881 x the orb's diameter offset
 * towards one edge; the lower orb is rotated 78.4deg, which carries its
 * gradient centre outside the circle.
 *
 * Do not read the radius off `absoluteBoundingBox`. For a rotated node Figma
 * reports the axis-aligned box of the *rotated frame*, so the lower orb's
 * 244.5 is 207 x (|cos| + |sin|) of its rotation, not its diameter.
 * `absoluteRenderBounds` gives the real 207.04.
 *
 * They sit under the wave: where the wave paints, it covers them.
 * ---------------------------------------------------------------------- */

export type Orb = {
  readonly id: string;
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
  /** Centre and radius of the radial gradient laid over the flat red. */
  readonly glow: { readonly cx: number; readonly cy: number; readonly r: number };
};

export const ORBS: readonly Orb[] = [
  { id: "upper", cx: 326.5, cy: 313.5, r: 103.5, glow: { cx: 377.99, cy: 374.5, r: 182.38 } },
  { id: "lower", cx: 64.26, cy: 696.26, r: 103.52, glow: { cx: 14.92, cy: 759.04, r: 182.42 } },
];

export const ORB_RED = "#FF0000";
export const ORB_RED_OPACITY = 0.92;
export const ORB_GLOW_OPACITY = 0.48;

/** Stops of the radial gradient laid over each orb. */
export const ORB_GLOW_STOPS = [
  { offset: 0, color: "#000000", opacity: 0.81 },
  { offset: 0.5, color: "#7B5384", opacity: 1 },
  { offset: 1, color: "#FFCC40", opacity: 0.78 },
] as const;

/* -------------------------------------------------------------------------
 * The hero's blob (36:70)
 *
 * An edited ellipse, so Figma stores it as a vector: the path below is its own
 * geometry, and `transform` is the rotation that puts it on the frame. Figma
 * reports the node rotated -15.2deg with a 281.86x187.08 box, which is the
 * axis-aligned box of the *rotated* 258.49x123.61 frame — rotating the path
 * and landing its box on 36:70's `absoluteRenderBounds` recovers the transform
 * exactly.
 *
 * Figma has a GLASS effect on it, but the fill is opaque black, so there is
 * nothing for the glass to refract and nothing here to reproduce.
 * ---------------------------------------------------------------------- */

export const HERO_BLOB = {
  /** Its box on the frame, which is what the SVG that carries it is drawn at. */
  box: { x: 66, y: -26, width: 281.86, height: 187.08 },
  transform: "translate(66 41.7974) rotate(-15.20565)",
  path:
    "M258.437 76.1306C260.419 109.326 204.183 129.823 132.832 121.91" +
    "C61.4799 113.998 2.03208 80.6734 0.0507864 47.4777C-1.93051 14.2819 54.305 -6.21442 125.657 1.69786" +
    "C197.008 9.61014 256.456 42.9349 258.437 76.1306Z",
} as const satisfies { box: Box; transform: string; path: string };
